// In-browser HRMS service. Mirrors the old REST API, but reads and writes a
// localStorage-backed store. Every call is async, so a real backend can be
// swapped in later without touching the pages.
import { db, persist, resetDb } from "@/data/db";
import {
  USERS, HOLIDAYS, LEAVE_ENTITLEMENT, uid, isoDate, addDays, round2, computeSalary, slipFor, certificateFor,
} from "@/data/seed";

const SESSION_KEY = "medilearn_hrms_session";
const LATENCY_MS = 120;

const respond = (fn) => new Promise((resolve, reject) => {
  setTimeout(() => {
    try { resolve(fn()); } catch (e) { reject(e); }
  }, LATENCY_MS);
});

const fail = (message) => { throw new Error(message); };
const clone = (x) => JSON.parse(JSON.stringify(x));
const today = () => isoDate(new Date());
const byDesc = (key) => (a, b) => (a[key] < b[key] ? 1 : a[key] > b[key] ? -1 : 0);

const publicUser = ({ password, ...u }) => u;

function findEmployee(id) {
  return db().employees.find((e) => e.id === id) || fail("Employee not found");
}

function teamIds(user) {
  return db().employees.filter((e) => e.reporting_head_id === user.employee_id).map((e) => e.id);
}

function logEmail(to, subject, body, type) {
  db().email_logs.unshift({ id: uid("EM"), to, subject, body, type, created_at: new Date().toISOString() });
}

function leaveBalance(employeeId) {
  const used = {};
  db().leaves
    .filter((l) => l.employee_id === employeeId && l.status === "Approved")
    .forEach((l) => { used[l.leave_type] = (used[l.leave_type] || 0) + l.days; });
  return Object.entries(LEAVE_ENTITLEMENT).map(([leave_type, entitled]) => {
    const u = used[leave_type] || 0;
    return { leave_type, entitled, used: u, balance: Math.max(entitled - u, 0) };
  });
}

function documentsExpiring() {
  const t = today();
  const soon = isoDate(addDays(new Date(), 30));
  const fields = [
    ["emirates_id_expiry", "Emirates ID"], ["passport_expiry", "Passport"], ["visa_expiry", "Visa"],
    ["labour_card_expiry", "Labour Card"], ["insurance_expiry", "Insurance"],
  ];
  const out = [];
  for (const e of db().employees) {
    for (const [field, document] of fields) {
      const exp = e[field];
      if (exp && exp <= soon) {
        out.push({ employee_id: e.id, employee_name: e.name, document, expiry_date: exp, status: exp < t ? "Expired" : "Expiring Soon" });
      }
    }
  }
  return out.sort((a, b) => a.expiry_date.localeCompare(b.expiry_date));
}

// ---------------------------------------------------------------- auth
export const auth = {
  login: (email, password) => respond(() => {
    const u = USERS.find((x) => x.email === email.trim().toLowerCase() && x.password === password);
    if (!u) fail("Invalid email or password");
    localStorage.setItem(SESSION_KEY, u.id);
    return publicUser(u);
  }),
  me: () => respond(() => {
    const id = localStorage.getItem(SESSION_KEY);
    const u = USERS.find((x) => x.id === id);
    return u ? publicUser(u) : null;
  }),
  logout: () => localStorage.removeItem(SESSION_KEY),
};

// ---------------------------------------------------------------- employees
export const employees = {
  list: (user) => respond(() => {
    let list = db().employees;
    if (user?.role === "manager") {
      const ids = teamIds(user);
      if (ids.length) list = list.filter((e) => ids.includes(e.id));
    }
    return clone(list);
  }),
  get: (id) => respond(() => clone(findEmployee(id))),
};

export const holidays = {
  list: () => respond(() => clone(HOLIDAYS)),
};

// ---------------------------------------------------------------- leaves
export const leaves = {
  list: (user) => respond(() => {
    let list = db().leaves;
    if (user.role === "employee") list = list.filter((l) => l.employee_id === user.employee_id);
    else if (user.role === "manager") {
      const ids = teamIds(user);
      if (ids.length) list = list.filter((l) => ids.includes(l.employee_id));
    }
    return clone([...list].sort(byDesc("created_at")));
  }),

  balance: (employeeId) => respond(() => leaveBalance(employeeId)),

  apply: ({ employee_id, leave_type, from_date, to_date, half_day, reason }) => respond(() => {
    const e = findEmployee(employee_id);
    if (to_date < from_date) fail("End date must be on or after start date");
    const span = Math.round((new Date(to_date) - new Date(from_date)) / 86400000) + 1;
    const days = half_day ? 0.5 : Math.max(span, 1);
    const leave = {
      id: uid("LV"), employee_id, employee_name: e.name, department: e.department,
      reporting_head_id: e.reporting_head_id, leave_type, from_date, to_date, half_day, days, reason,
      status: "Pending", rejection_reason: null, created_at: new Date().toISOString(), decided_at: null, decided_by: null,
    };
    db().leaves.push(leave);
    logEmail(e.email, `Leave Applied — ${leave_type}`, `Your leave request from ${from_date} to ${to_date} (${days} days) has been submitted.`, "leave_applied");
    persist();
    return clone(leave);
  }),

  decide: (id, decision, actor, reason) => respond(() => {
    const l = db().leaves.find((x) => x.id === id) || fail("Leave not found");
    if (l.status !== "Pending") fail("Leave is not pending");
    l.status = decision;
    l.decided_at = new Date().toISOString();
    l.decided_by = actor.name;
    if (decision === "Rejected") l.rejection_reason = reason || "Not specified";
    const to = findEmployee(l.employee_id).email;
    logEmail(to, `Leave ${decision}`,
      decision === "Approved"
        ? `${l.employee_name}'s ${l.leave_type} leave has been approved.`
        : `${l.employee_name}'s ${l.leave_type} leave was rejected. Reason: ${l.rejection_reason}`,
      decision === "Approved" ? "leave_approved" : "leave_rejected");
    persist();
  }),

  cancel: (id) => respond(() => {
    const l = db().leaves.find((x) => x.id === id) || fail("Leave not found");
    if (l.status !== "Pending") fail("Only pending leaves can be cancelled");
    l.status = "Cancelled";
    persist();
  }),
};

// ---------------------------------------------------------------- attendance
function todayRecord(employeeId) {
  return db().attendance.find((a) => a.employee_id === employeeId && a.date === today());
}

export const attendance = {
  today: (employeeId) => respond(() => clone(todayRecord(employeeId) || {
    employee_id: employeeId, date: today(), punch_in: null, punch_out: null, working_hours: 0, status: "Not Marked",
  })),

  punchIn: (employeeId, location = "Dubai Office, Business Bay") => respond(() => {
    if (todayRecord(employeeId)?.punch_in) fail("Already punched in today");
    const rec = {
      id: uid("AT"), employee_id: employeeId, date: today(), punch_in: new Date().toISOString(),
      punch_out: null, working_hours: 0, status: "Present", late: false, location,
    };
    db().attendance.push(rec);
    persist();
    return clone(rec);
  }),

  punchOut: (employeeId) => respond(() => {
    const rec = todayRecord(employeeId);
    if (!rec?.punch_in) fail("Punch in first");
    if (rec.punch_out) fail("Already punched out today");
    rec.punch_out = new Date().toISOString();
    rec.working_hours = round2((new Date(rec.punch_out) - new Date(rec.punch_in)) / 3600000);
    persist();
    return clone(rec);
  }),

  list: ({ employeeId, month } = {}) => respond(() => {
    let list = db().attendance;
    if (employeeId) list = list.filter((a) => a.employee_id === employeeId);
    if (month) list = list.filter((a) => a.date.startsWith(month));
    return clone([...list].sort(byDesc("date")));
  }),

  monthly: (employeeId, month) => respond(() => {
    const records = db().attendance
      .filter((a) => a.employee_id === employeeId && a.date.startsWith(month))
      .sort(byDesc("date"));
    const count = (s) => records.filter((r) => r.status === s).length;
    return clone({
      total_working_days: 22,
      present: count("Present"), absent: count("Absent"), half_day: count("Half Day"), on_leave: count("On Leave"),
      late: records.filter((r) => r.late).length,
      total_hours: round2(records.reduce((s, r) => s + (r.working_hours || 0), 0)),
      records,
    });
  }),
};

// ---------------------------------------------------------------- payroll
export const salarySlips = {
  list: (user, { employeeId, month } = {}) => respond(() => {
    let list = db().salary_slips;
    if (user.role === "employee") list = list.filter((s) => s.employee_id === user.employee_id);
    else if (employeeId) list = list.filter((s) => s.employee_id === employeeId);
    if (month) list = list.filter((s) => s.month === month);
    return clone([...list].sort((a, b) => b.month.localeCompare(a.month) || a.employee_name.localeCompare(b.employee_name)));
  }),

  get: (id) => respond(() => clone(db().salary_slips.find((s) => s.id === id) || fail("Salary slip not found"))),

  create: ({ employee_id, month, ...components }) => respond(() => {
    const e = findEmployee(employee_id);
    if (db().salary_slips.some((s) => s.employee_id === employee_id && s.month === month)) {
      fail(`A slip for ${e.name} already exists for ${month}`);
    }
    const slip = slipFor(e, month, components);
    db().salary_slips.push(slip);
    logEmail(e.email, `Salary Slip — ${month}`, `Your salary slip for ${month} is now available. Net pay: AED ${slip.net_pay.toLocaleString()}.`, "salary_slip");
    persist();
    return clone(slip);
  }),

  setWpsStatus: (id, status) => respond(() => {
    const s = db().salary_slips.find((x) => x.id === id) || fail("Salary slip not found");
    s.wps_status = status;
    persist();
  }),

  preview: computeSalary,
};

export const certificates = {
  list: (user) => respond(() => {
    let list = db().salary_certificates;
    if (user.role === "employee") list = list.filter((c) => c.employee_id === user.employee_id);
    return clone([...list].sort(byDesc("created_at")));
  }),
  get: (id) => respond(() => clone(db().salary_certificates.find((c) => c.id === id) || fail("Certificate not found"))),
  request: ({ employee_id, purpose, addressed_to }) => respond(() => {
    if (!addressed_to?.trim()) fail("Enter who the certificate is addressed to");
    const cert = certificateFor(findEmployee(employee_id), purpose, addressed_to.trim());
    db().salary_certificates.push(cert);
    persist();
    return clone(cert);
  }),
};

// ---------------------------------------------------------------- dashboards
export const dashboard = {
  admin: () => respond(() => {
    const d = db(), t = today();
    const onLeaveToday = d.leaves.filter((l) => l.status === "Approved" && l.from_date <= t && l.to_date >= t).length;
    const presentToday = d.attendance.filter((a) => a.date === t && a.punch_in).length;
    const count = (s) => d.leaves.filter((l) => l.status === s).length;

    const trend = {}, type = {}, dept = {};
    for (const l of d.leaves) {
      const m = l.from_date.slice(0, 7);
      trend[m] = (trend[m] || 0) + 1;
      type[l.leave_type] = (type[l.leave_type] || 0) + l.days;
      dept[l.department] = (dept[l.department] || 0) + l.days;
    }
    const currentMonth = t.slice(0, 7);
    const monthSlips = d.salary_slips.filter((s) => s.month === currentMonth);

    return clone({
      total_employees: d.employees.length,
      present_today: presentToday,
      absent_today: Math.max(d.employees.length - presentToday - onLeaveToday, 0),
      on_leave_today: onLeaveToday,
      pending_approvals: count("Pending"),
      approved_leaves: count("Approved"),
      rejected_leaves: count("Rejected"),
      salary_slips_count: monthSlips.length,
      monthly_payroll_aed: round2(monthSlips.reduce((s, x) => s + x.net_pay, 0)),
      wps_ready_count: monthSlips.filter((s) => ["Ready for WPS", "Processed", "Paid"].includes(s.wps_status)).length,
      documents_expiring: documentsExpiring(),
      documents_tracked: d.employees.length * 5,
      upcoming_holidays: HOLIDAYS.filter((h) => h.date >= t).slice(0, 10),
      leave_trend: Object.entries(trend).sort().map(([month, count]) => ({ month, count })),
      leave_type_usage: Object.entries(type).map(([type, days]) => ({ type, days })),
      dept_usage: Object.entries(dept).map(([dept, days]) => ({ dept, days })),
    });
  }),

  employee: (employeeId) => respond(() => {
    const d = db(), t = today();
    return clone({
      employee: findEmployee(employeeId),
      today_attendance: todayRecord(employeeId) || null,
      leave_balance: leaveBalance(employeeId),
      upcoming_holidays: HOLIDAYS.filter((h) => h.date >= t).slice(0, 5),
      recent_slips: d.salary_slips.filter((s) => s.employee_id === employeeId).sort((a, b) => b.month.localeCompare(a.month)).slice(0, 6),
      pending_leaves: d.leaves.filter((l) => l.employee_id === employeeId && l.status === "Pending").length,
    });
  }),
};

export const notifications = {
  list: () => respond(() => clone(db().email_logs.slice(0, 50))),
};

export const demo = {
  reset: () => respond(() => resetDb()),
};
